/**
 * Notification Settings Component
 *
 * Shared component for managing notification preferences:
 * - Master toggle for push notifications
 * - Individual toggles for each notification type
 * - Email digest toggle
 * - Master toggle for email notifications
 * - Save button to update preferences via API
 */

import React, { useState, useEffect } from 'react';
import { Save, Bell, BellOff, Mail, Smartphone } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { supabase } from '../../lib/supabaseClient';

export function NotificationSettings() {
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/notifications/preferences`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch preferences');
      }

      const data = await response.json();

      if (data.success) {
        setPreferences(data.preferences);
      }

      setLoading(false);

    } catch (error) {
      console.error('[Settings] Fetch preferences error:', error);
      setMessage({ type: 'error', text: 'Failed to load notification settings' });
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      const data = await response.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Notification settings saved successfully!' });
        setPreferences(data.preferences);

        // Clear success message after 3 seconds
        setTimeout(() => {
          setMessage({ type: '', text: '' });
        }, 3000);
      }

    } catch (error) {
      console.error('[Settings] Save preferences error:', error);
      setMessage({ type: 'error', text: 'Failed to save notification settings' });
    } finally {
      setSaving(false);
    }
  };

  const updatePreference = (field, value) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-slate-600">Loading notification settings...</p>
        </CardContent>
      </Card>
    );
  }

  if (!preferences) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <p className="text-slate-600">Failed to load notification settings</p>
          <Button onClick={fetchPreferences} className="mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Push Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Smartphone className="w-5 h-5 text-slate-600" />
            <div>
              <CardTitle>Push Notifications</CardTitle>
              <CardDescription>
                Receive instant notifications on your mobile device
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Master toggle */}
          <div className="flex items-center justify-between pb-4 border-b">
            <div className="flex items-center gap-3">
              {preferences.push_enabled ? (
                <Bell className="w-5 h-5 text-blue-600" />
              ) : (
                <BellOff className="w-5 h-5 text-slate-400" />
              )}
              <div>
                <h4 className="font-semibold text-sm">Enable Push Notifications</h4>
                <p className="text-sm text-slate-600">
                  Master switch for all push notifications
                </p>
              </div>
            </div>
            <Switch
              checked={preferences.push_enabled}
              onCheckedChange={(checked) => updatePreference('push_enabled', checked)}
            />
          </div>

          {/* Individual notification types */}
          <div className="space-y-3 opacity-${preferences.push_enabled ? '100' : '50'}">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">RFQ Status Changes</h4>
                <p className="text-xs text-slate-600">
                  Not invited → Inclusion requested → Invited
                </p>
              </div>
              <Switch
                checked={preferences.push_rfq_status_changes}
                onCheckedChange={(checked) => updatePreference('push_rfq_status_changes', checked)}
                disabled={!preferences.push_enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">New Applications</h4>
                <p className="text-xs text-slate-600">
                  When candidates apply to RFQs
                </p>
              </div>
              <Switch
                checked={preferences.push_new_applications}
                onCheckedChange={(checked) => updatePreference('push_new_applications', checked)}
                disabled={!preferences.push_enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">Candidate Detail Requests</h4>
                <p className="text-xs text-slate-600">
                  When clients request candidate information
                </p>
              </div>
              <Switch
                checked={preferences.push_candidate_requests}
                onCheckedChange={(checked) => updatePreference('push_candidate_requests', checked)}
                disabled={!preferences.push_enabled}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">New Messages</h4>
                <p className="text-xs text-slate-600">
                  When you receive new messages (future feature)
                </p>
              </div>
              <Switch
                checked={preferences.push_new_messages}
                onCheckedChange={(checked) => updatePreference('push_new_messages', checked)}
                disabled={!preferences.push_enabled}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Notifications */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <Mail className="w-5 h-5 text-slate-600" />
            <div>
              <CardTitle>Email Notifications</CardTitle>
              <CardDescription>
                Receive notification summaries via email
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Master toggle */}
          <div className="flex items-center justify-between pb-4 border-b">
            <div>
              <h4 className="font-semibold text-sm">Enable Email Notifications</h4>
              <p className="text-sm text-slate-600">
                Master switch for all email notifications
              </p>
            </div>
            <Switch
              checked={preferences.email_enabled}
              onCheckedChange={(checked) => updatePreference('email_enabled', checked)}
            />
          </div>

          {/* Email digest */}
          <div className="space-y-3 opacity-${preferences.email_enabled ? '100' : '50'}">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">Daily Digest</h4>
                <p className="text-xs text-slate-600">
                  Summary of unread notifications sent daily
                </p>
              </div>
              <Switch
                checked={preferences.email_digest}
                onCheckedChange={(checked) => updatePreference('email_digest', checked)}
                disabled={!preferences.email_enabled}
              />
            </div>

            {preferences.email_digest && (
              <div className="ml-4 pl-4 border-l-2 border-slate-200">
                <label className="text-sm font-medium text-slate-700 block mb-2">
                  Frequency
                </label>
                <select
                  value={preferences.email_digest_frequency}
                  onChange={(e) => updatePreference('email_digest_frequency', e.target.value)}
                  disabled={!preferences.email_enabled || !preferences.email_digest}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="never">Never</option>
                </select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Save button and messages */}
      <div className="flex items-center justify-between">
        <div>
          {message.text && (
            <p className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </p>
          )}
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}
